import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {Map as ImmutableMap} from 'immutable';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {formatCurrencyValue} from './ACCBenefits';
import {accidentInjuriesPath} from './PropertyPaths/accidentInjuriesPath';

export const BurnDetails = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentInjuriesPath(selectedPlan?.planIndex || 0, 0);

  const productCd = selectedProduct?.code ?? '';

  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) === 'GVA6';

  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'burn-details-row-1'},
      items: [
        {
          key: propertyPaths.burnDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label={isGVA6Plan ? 'Incurral Period (Days)' : 'Incurral Period (Hours)'}
              value={getQuoteFieldValue<number>(propertyPaths.burnDetails.incurralPeriod)}
              disabled
              id={propertyPaths.burnDetails.incurralPeriod}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.burnDetails.customizeBenefitAmount,
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.burnDetails.customizeBenefitAmount)}
              id={propertyPaths.burnDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'burn-degree-row-1'},
      items: [
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[0].burnSize1Amount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label={isGVA6Plan ? 'Less Than 15% of body' : 'Less than 10% of Burned Skin Surface (Degree 2)'}
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[0].burnSize1Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[0].burnSize1Amount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[1].burnSize1Amount,
          hidden: isGVA6Plan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Less than 10% of Burned Skin Surface (Degree 3)"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[1].burnSize1Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[1].burnSize1Amount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'burn-degree-row-2'},
      items: [
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[0].burnSize2Amount,
          hidden: situsStateCd === 'NH',
          element: (
            <QuoteFormElement
              elementType="Text"
              label={
                isGVA6Plan
                  ? 'Equal or More than 15% of body'
                  : 'At Least 10% but Less Than 25% of Burned Skin Surface (Degree 2)'
              }
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[0].burnSize2Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[0].burnSize2Amount}
              adornment="money"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[1].burnSize2Amount,
          hidden: isGVA6Plan || situsStateCd === 'NH',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="At Least 10% but Less Than 25% of Burned Skin Surface (Degree 3)"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[1].burnSize2Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[1].burnSize2Amount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'burn-degree-row-2'},
      items: [
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[0].burnSize3Amount,
          hidden: isGVA6Plan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="25% or More of Burned Skin Surface (Degree 2)"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[0].burnSize3Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[0].burnSize3Amount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.burnDetails.burnDegreeDetails[1].burnSize3Amount,
          hidden: isGVA6Plan,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="25% or More of Burned Skin Surface (Degree 3)"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.burnDetails.burnDegreeDetails[1].burnSize3Amount)
              )}
              id={propertyPaths.burnDetails.burnDegreeDetails[1].burnSize3Amount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'burn-details-row-6'},
      items: [
        {
          key: propertyPaths.burnDetails.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Each Benefit is Payable per Time Period"
              searchKey={`${productCd}BurnBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.burnDetails.maxBenefitNumber)}
              disabled
              id={propertyPaths.burnDetails.maxBenefitNumber}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.burnDetails.timePeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`${productCd}BurnBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.burnDetails.timePeriodCd)}
              disabled
              id={propertyPaths.burnDetails.timePeriodCd}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};
